/**
 * Utility for persisting query string parameters to a cookie for referral tracking
 */

const COOKIE_NAME = "pl_qs_params"
const COOKIE_DOMAIN = ".pitchlane.com"
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60 // 30 days in seconds

/**
 * Persist all query string parameters to a cookie on .pitchlane.com domain
 * This allows query params to be preserved across redirects and subdomains
 */
export function persistQueryParamsToCookie() {
    const params = new URLSearchParams(window.location.search)

    // Only set cookie if there are query parameters
    if (params.toString()) {
        const existingCookie = getQueryParamsFromCookie()

        // Merge new params with existing ones (new params take precedence)
        const mergedParams = { ...existingCookie }
        params.forEach((value, key) => {
            mergedParams[key] = value
        })

        const cookieValue = JSON.stringify(mergedParams)

        // Set cookie on .pitchlane.com domain with secure flags
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(cookieValue)}; domain=${COOKIE_DOMAIN}; max-age=${COOKIE_MAX_AGE}; path=/; secure; samesite=lax`
    }
}

/**
 * Get query parameters from the cookie
 * @returns Object with query parameters or empty object if not found
 */
export function getQueryParamsFromCookie(): Record<string, string> {
    const cookies = document.cookie.split(";")

    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=")
        if (name === COOKIE_NAME && value) {
            try {
                return JSON.parse(decodeURIComponent(value))
            } catch (e) {
                console.error("Error parsing query params cookie:", e)
                return {}
            }
        }
    }

    return {}
}

/**
 * Extract UTM parameters from the cookie
 */
export function getUtmParamsFromCookie(): Record<string, string> {
    const allParams = getQueryParamsFromCookie()
    const utmParams: Record<string, string> = {}

    Object.keys(allParams).forEach((key) => {
        if (key.startsWith("utm_")) {
            utmParams[key] = allParams[key]
        }
    })

    return utmParams
}

/**
 * Get HTTP referrer from the cookie (forwarded as pl_ref parameter)
 * @returns The HTTP referrer URL or undefined if not found
 */
export function getHttpReferrerFromCookie(): string | undefined {
    const allParams = getQueryParamsFromCookie()
    return allParams.pl_ref
}

/**
 * Clear the query params cookie
 */
export function clearQueryParamsCookie() {
    document.cookie = `${COOKIE_NAME}=; domain=${COOKIE_DOMAIN}; max-age=0; path=/`
}
